import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Flame, CalendarDays } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { readGuestDaily, guestStreak, lastNDates, utcToday } from "../lib/guest";

const DOW = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

export function WeeklyRecap() {
  const { user } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    if (user === null) return;
    if (user === false) {
      const saved = readGuestDaily();
      const today = utcToday();
      setData({ today, byDate: saved, streak: guestStreak(new Set(Object.keys(saved)), today) });
      return;
    }
    api.get("/daily/history", { params: { days: 7 } })
      .then((r) => setData({
        today: r.data.today,
        byDate: Object.fromEntries(r.data.days.map((d) => [d.date, d])),
        streak: r.data.streak,
      }))
      .catch(() => setData({ today: utcToday(), byDate: {}, streak: 0 }));
  }, [user]);

  const today = data?.today || utcToday();
  const dates = lastNDates(7, today);
  const playedToday = !!data?.byDate?.[today];

  return (
    <div className="panel p-5 mb-10" data-testid="weekly-recap">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <CalendarDays className="w-5 h-5 text-phosphor" />
          <div>
            <div className="text-[10px] tracking-[0.3em] text-muted-foreground uppercase">Last 7 days · Daily Challenge</div>
            <div className="text-sm font-semibold flex items-center gap-2" data-testid="weekly-streak">
              <Flame className={`w-4 h-4 ${data?.streak ? "text-amber-300" : "text-muted-foreground"}`} />
              {data?.streak || 0}-day streak
            </div>
          </div>
        </div>
        <Link
          to="/daily"
          data-testid="weekly-recap-cta"
          className={`px-4 py-2 text-[10px] tracking-[0.25em] uppercase font-semibold ${
            playedToday ? "border border-phosphor/40 text-muted-foreground hover:text-foreground" : "bg-primary text-primary-foreground hover:bg-primary/90"
          }`}
        >
          {playedToday ? "Today logged" : "Play today"}
        </Link>
      </div>
      <div className="grid grid-cols-7 gap-2">
        {dates.map((d) => {
          const r = data?.byDate?.[d];
          const isToday = d === today;
          return (
            <div
              key={d}
              data-testid={`recap-day-${d}`}
              title={r ? `${d}: ${r.score} pts · ${r.correct}/${r.total}` : `${d}: not played`}
              className={`border p-2 text-center ${
                r ? "border-phosphor bg-primary/10" : isToday ? "border-amber-400/60 border-dashed" : "border-border"
              }`}
            >
              <div className="text-[9px] tracking-widest text-muted-foreground">{DOW[new Date(`${d}T00:00:00Z`).getUTCDay()]}</div>
              <div className={`text-lg font-bold leading-tight ${r ? "text-phosphor" : "text-muted-foreground/50"}`}>{r ? r.score : "–"}</div>
              <div className="text-[9px] text-muted-foreground">{r ? `${r.correct}/${r.total}` : isToday ? "today" : d.slice(5)}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
